// frontend/src/utils/treasuryExports.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Formater une devise
 */
const formatCurrency = (amount, currency = 'TND') => {
  const formatted = new Intl.NumberFormat('fr-TN', {
    style: 'currency',
    currency: currency,
  }).format(amount);
  // Remplacer les espaces insécables ( ,  ) par des espaces normaux
  // pour éviter l'affichage du caractère / dans jsPDF
  return formatted.replace(/[  ]/g, ' ');
};

/**
 * Formater une date
 */
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Obtenir le libellé de catégorie
 */
const getCategoryLabel = (category) => {
  const labels = {
    sale: 'Vente',
    purchase: 'Achat',
    salary: 'Salaire',
    rent: 'Loyer',
    utilities: 'Charges',
    tax: 'Impôts',
    loan: 'Prêt',
    investment: 'Investissement',
    withdrawal: 'Retrait',
    deposit: 'Dépôt',
    transfer: 'Virement',
    other: 'Autre',
  };
  return labels[category] || category;
};

/**
 * Exporter les transactions d'un compte en PDF
 */
export const exportToPDF = (account, transactions, type = 'cash') => {
  const doc = new jsPDF();
  
  // En-tête
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(type === 'cash' ? 'RELEVÉ DE CAISSE' : 'RELEVÉ BANCAIRE', 105, 20, { align: 'center' });
  
  // Informations du compte
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Compte : ${account.name}`, 14, 35);
  
  if (type === 'bank') {
    doc.text(`Banque : ${account.bankName}`, 14, 42);
    doc.text(`N° Compte : ${account.accountNumber}`, 14, 49);
    if (account.rib) doc.text(`RIB : ${account.rib}`, 14, 56);
  } else {
    if (account.location) doc.text(`Emplacement : ${account.location}`, 14, 42);
  }
  
  doc.text(`Date d'édition : ${new Date().toLocaleDateString('fr-FR')}`, 14, type === 'bank' ? 63 : 49);
  
  // Solde
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const soldeY = type === 'bank' ? 70 : 56;
  doc.text(`Solde actuel : ${formatCurrency(account.currentBalance, account.currency)}`, 14, soldeY);
  
  // Tableau des transactions
  const tableData = transactions.map(t => [
    formatDate(t.date),
    t.description,
    getCategoryLabel(t.category),
    t.transactionType === 'income' 
      ? formatCurrency(t.amount, account.currency)
      : '',
    t.transactionType === 'expense' 
      ? formatCurrency(t.amount, account.currency)
      : '',
  ]);
  
  autoTable(doc, {
    startY: soldeY + 10,
    head: [['Date', 'Description', 'Catégorie', 'Entrée', 'Sortie']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 60 },
      2: { cellWidth: 35 },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
    },
  });
  
  // Totaux
  const finalY = doc.lastAutoTable.finalY + 10;
  const totalEntrees = transactions
    .filter(t => t.transactionType === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSorties = transactions
    .filter(t => t.transactionType === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
  
  doc.setFont('helvetica', 'bold');
  doc.text(`Total Entrées : ${formatCurrency(totalEntrees, account.currency)}`, 14, finalY);
  doc.text(`Total Sorties : ${formatCurrency(totalSorties, account.currency)}`, 14, finalY + 7);
  doc.text(`Solde : ${formatCurrency(totalEntrees - totalSorties, account.currency)}`, 14, finalY + 14);
  
  // Pied de page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${i} sur ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
  
  // Télécharger
  const filename = `${type === 'cash' ? 'caisse' : 'banque'}_${account.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};

/**
 * Exporter les transactions d'un compte en Excel
 */
export const exportToExcel = (account, transactions, type = 'cash') => {
  // Préparer les données
  const data = [
    // En-tête
    [type === 'cash' ? 'RELEVÉ DE CAISSE' : 'RELEVÉ BANCAIRE'],
    [],
    ['Compte :', account.name],
  ];
  
  if (type === 'bank') {
    data.push(
      ['Banque :', account.bankName],
      ['N° Compte :', account.accountNumber]
    );
    if (account.rib) data.push(['RIB :', account.rib]);
  } else {
    if (account.location) data.push(['Emplacement :', account.location]);
  }
  
  data.push(
    ['Date d\'édition :', new Date().toLocaleDateString('fr-FR')],
    ['Solde actuel :', formatCurrency(account.currentBalance, account.currency)],
    [],
    // En-têtes du tableau
    ['Date', 'Description', 'Catégorie', 'Référence', 'Entrée', 'Sortie', 'Notes']
  );
  
  // Transactions
  transactions.forEach(t => {
    data.push([
      formatDate(t.date),
      t.description,
      getCategoryLabel(t.category),
      t.reference || '',
      t.transactionType === 'income' ? t.amount : '',
      t.transactionType === 'expense' ? t.amount : '',
      t.notes || '',
    ]);
  });
  
  // Ligne vide
  data.push([]);
  
  // Totaux
  const totalEntrees = transactions
    .filter(t => t.transactionType === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSorties = transactions
    .filter(t => t.transactionType === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
  
  data.push(
    ['', '', '', 'Total Entrées :', totalEntrees, '', ''],
    ['', '', '', 'Total Sorties :', '', totalSorties, ''],
    ['', '', '', 'Solde :', totalEntrees - totalSorties, '', '']
  );
  
  // Créer le workbook et la feuille
  const ws = XLSX.utils.aoa_to_sheet(data);
  
  // Définir les largeurs de colonnes
  ws['!cols'] = [
    { wch: 12 },  // Date
    { wch: 40 },  // Description
    { wch: 15 },  // Catégorie
    { wch: 15 },  // Référence
    { wch: 12 },  // Entrée
    { wch: 12 },  // Sortie
    { wch: 20 },  // Notes
  ];
  
  // Créer le workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Relevé');
  
  // Télécharger
  const filename = `${type === 'cash' ? 'caisse' : 'banque'}_${account.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, filename);
};

/**
 * Imprimer le relevé
 */
export const printStatement = (account, transactions, type = 'cash') => {
  const printWindow = window.open('', '_blank');
  
  const totalEntrees = transactions
    .filter(t => t.transactionType === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSorties = transactions
    .filter(t => t.transactionType === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Relevé ${type === 'cash' ? 'Caisse' : 'Banque'} - ${account.name}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #333;
        }
        h1 {
          text-align: center;
          color: #2c3e50;
          margin-bottom: 30px;
        }
        .header-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 5px;
          margin-bottom: 20px;
        }
        .header-info p {
          margin: 5px 0;
        }
        .solde {
          font-size: 18px;
          font-weight: bold;
          color: #27ae60;
          margin-top: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }
        th {
          background-color: #2980b9;
          color: white;
          padding: 10px;
          text-align: left;
        }
        td {
          padding: 8px;
          border-bottom: 1px solid #ddd;
        }
        tr:nth-child(even) {
          background-color: #f2f2f2;
        }
        .text-right {
          text-align: right;
        }
        .income {
          color: #27ae60;
          font-weight: bold;
        }
        .expense {
          color: #e74c3c;
          font-weight: bold;
        }
        .totals {
          margin-top: 20px;
          padding: 15px;
          background: #ecf0f1;
          border-radius: 5px;
        }
        .totals p {
          margin: 5px 0;
          font-weight: bold;
        }
        @media print {
          button {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <h1>${type === 'cash' ? 'RELEVÉ DE CAISSE' : 'RELEVÉ BANCAIRE'}</h1>
      
      <div class="header-info">
        <p><strong>Compte :</strong> ${account.name}</p>
        ${type === 'bank' ? `
          <p><strong>Banque :</strong> ${account.bankName}</p>
          <p><strong>N° Compte :</strong> ${account.accountNumber}</p>
          ${account.rib ? `<p><strong>RIB :</strong> ${account.rib}</p>` : ''}
        ` : `
          ${account.location ? `<p><strong>Emplacement :</strong> ${account.location}</p>` : ''}
        `}
        <p><strong>Date d'édition :</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
        <p class="solde">Solde actuel : ${formatCurrency(account.currentBalance, account.currency)}</p>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Catégorie</th>
            <th class="text-right">Entrée</th>
            <th class="text-right">Sortie</th>
          </tr>
        </thead>
        <tbody>
          ${transactions.map(t => `
            <tr>
              <td>${formatDate(t.date)}</td>
              <td>${t.description}${t.reference ? ` <small>(${t.reference})</small>` : ''}</td>
              <td>${getCategoryLabel(t.category)}</td>
              <td class="text-right ${t.transactionType === 'income' ? 'income' : ''}">
                ${t.transactionType === 'income' ? formatCurrency(t.amount, account.currency) : ''}
              </td>
              <td class="text-right ${t.transactionType === 'expense' ? 'expense' : ''}">
                ${t.transactionType === 'expense' ? formatCurrency(t.amount, account.currency) : ''}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div class="totals">
        <p>Total Entrées : <span class="income">${formatCurrency(totalEntrees, account.currency)}</span></p>
        <p>Total Sorties : <span class="expense">${formatCurrency(totalSorties, account.currency)}</span></p>
        <p>Solde : ${formatCurrency(totalEntrees - totalSorties, account.currency)}</p>
      </div>
      
      <script>
        window.onload = function() {
          window.print();
        };
      </script>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
};